import * as React from 'react';

import * as _ from 'lodash';

import { Button, ButtonContainer, Checkbox, Link, Typography } from '@emburse/ui-core';

import { DatePickerEmbark } from '@ReactComponents/Shared/DatePicker/DatePickerEmbark';

import { ICardExtension } from '@ClientModels/card_extension';
import { FULLMONTHNAME_DAY_YEAR } from '@Shared/constants';
import { formatDate } from '@Shared/helpers/abacusTimeFormatter';
import { IUserModel } from '@Shared/models';

// Styles
import 'react-dates/lib/css/_datepicker.css';

import * as style from './BulkSkipPanel.styl';

interface IBulkSkipPanelProps {
  cards: ICardExtension[];
  usersByCardId: Record<string, IUserModel>;
  onBulkSkipConfirmed: (beforeDate: Date, accountIds: string[]) => void;
  onCancel: () => void;
  onError: (err: string) => void;
}

interface IBulkSkipPanelState {
  selectedCardMap: Record<string, boolean>;
  focused: boolean;
  confirmation: boolean;
  beforeDate?: Date;
  beforeDateFormatted?: string;
  numAccountIds?: number;
}

export class BulkSkipPanel extends React.Component<IBulkSkipPanelProps, IBulkSkipPanelState> {
  state: IBulkSkipPanelState = {
    selectedCardMap: {},
    focused: false,
    confirmation: false
  };

  renderEmbarkCardOptions(cards: ICardExtension[], usersByCardId: Record<string, IUserModel>) {
    return cards.map((card, index) => {
      const user = usersByCardId[card.map_id];

      return (
        <div key={index}>
          <div className="flex ai-c">
            <Checkbox
              id={`bulkSkip_${card.id}`}
              color="primary"
              key={this.state.selectedCardMap[card.map_id]}
              onChange={() => this.toggleSelection(card.map_id)}
              checked={this.state.selectedCardMap[card.map_id]}
            />
            <label htmlFor={`bulkSkip_${card.id}`}>
              <strong>{card.name}</strong>
            </label>
          </div>
          {user && <span className="assignee-user">{user.name}</span>}
        </div>
      );
    });
  }

  toggleSelection(cardId: string) {
    const selectedCardMap = _.assign({}, this.state.selectedCardMap);

    selectedCardMap[cardId] = !selectedCardMap[cardId];
    this.setState({ selectedCardMap });
  }

  selectAll(cards: ICardExtension[]) {
    const allSelected = cards.reduce((selectedCardMap: Record<string, boolean>, card) => {
      selectedCardMap[card.map_id] = true;

      return selectedCardMap;
    }, {});

    this.setState({ selectedCardMap: allSelected });
  }

  onSave(beforeDate: Date, selectedCardMap: Record<string, boolean>) {
    const accountIds = Object.keys(selectedCardMap).filter((cardId) => selectedCardMap[cardId]);

    if (beforeDate && accountIds.length) {
      if (this.state.confirmation) {
        return this.props.onBulkSkipConfirmed(beforeDate, accountIds);
      }

      // If there were errors from previous actions, OK to clear them now.
      this.props.onError(null);

      return this.setState({
        confirmation: true,
        beforeDateFormatted: formatDate(beforeDate, FULLMONTHNAME_DAY_YEAR),
        numAccountIds: accountIds.length
      });
    }

    return this.props.onError('Please select a date and one or more cards.');
  }

  renderEmbarkPanel(cards: ICardExtension[], usersByCardId: Record<string, IUserModel>) {
    if (this.state.confirmation) {
      return (
        <div>
          <Typography className="card-body">
            Are you sure you want to skip transactions for these {this.state.numAccountIds} card(s) through {this.state.beforeDateFormatted}
            ? Careful! This cannot be undone.
          </Typography>
        </div>
      );
    }
    return (
      <div className="card-body">
        <div className="date-input">
          <p>Skip all transactions through date</p>
          <div className="dateInputContainer embark-modal-date-picker">
            <div className="inputWrapper">
              <DatePickerEmbark
                id="null"
                label="Deactivation Date"
                value={this.state.beforeDate}
                onChange={(date) => this.setState({ beforeDate: date })}
                showClearDate
                disableFuture
                closeOnSelect
              />
            </div>
          </div>
        </div>
        <div className="select-cards">
          <p>
            For selected cards ( <Link onClick={() => this.selectAll(cards)}>Select all</Link> )
          </p>
          <ul className="listNoBullets bulk-skip-card-list">{this.renderEmbarkCardOptions(cards, usersByCardId)}</ul>
        </div>
      </div>
    );
  }

  renderEmbarkFooter(onCancel: () => void) {
    return (
      <ButtonContainer alignButtons="right" className="card-footer">
        <Button variant="outlined" onClick={() => onCancel()}>
          Cancel
        </Button>
        <Button onClick={() => this.onSave(this.state.beforeDate, this.state.selectedCardMap)}>
          {this.state.confirmation ? 'Apply' : 'Save'}
        </Button>
      </ButtonContainer>
    );
  }

  render() {
    const { cards, usersByCardId, onCancel } = this.props;

    return cards.length ? (
      <div className={style.global}>
        {this.renderEmbarkPanel(cards, usersByCardId)}
        {this.renderEmbarkFooter(onCancel)}
      </div>
    ) : (
      <p>You have no active cards as part of this card program.</p>
    );
  }
}
